export const AGENT_JUDGE_MANAGER = "manager_agentic_llm_judge"
export const AGENT_JUDGE_CATEGORICAL_MANAGER =
  "manager_agentic_llm_judge_categorical"
export const AGENT_JUDGE_NUMERICAL_MANAGER =
  "manager_agentic_llm_judge_numerical"

// Add new type for metric manager after the convertToSlug function
export type MetricManager =
  | typeof AGENT_JUDGE_MANAGER
  | typeof AGENT_JUDGE_CATEGORICAL_MANAGER
  | typeof AGENT_JUDGE_NUMERICAL_MANAGER
  | "manager_llm_binary"
  | "manager_toolcall"
  | "manager_default_categorical"
  | "manager_metadata_field_metric"
  | "manager_numerical_llm_judge"
  | "manager_audio_llm_judge"
  | "manager_audio_llm_binary"
  | "manager_audio_llm_categorical"
  | "manager_audio_llm_numerical"
  | "manager_transcript_regex_match"
  | "manager_user_frustration"
  | "manager_agent_refusal"
  | "manager_api_state"
  | "pause_analysis_metric"
  | "manager_volume"
  | "manager_match_expected_sim_wrapper"
  | "manager_vocal_fry"
  | "manager_abrupt_pitch_changes"
  | "manager_pitch_variability"
  | "manager_lufs"
  | "manager_terminal_contour"
  | "manager_volume_pitch_misalignment"
  | "manager_non_expressive_pauses"
  | "manager_spectrogram_pitch_analysis"
  | "manager_words_per_message_with_threshold"
  | "manager_composite_evaluation"
  | "manager_custom_trace"
  | "manager_agent_repeats_itself"
  | "manager_custom_audio_frequency"
  | "manager_custom_end_reason"
  | "manager_custom_agent_needs_reprompting"
  | "manager_custom_audio_sentiment"
  | "manager_custom_agent_fails_to_respond"
  | "manager_rubric_decision_tree"
  | "manager_ivr_flow_adherence"
  | "manager_transcription_error"
  | "manager_clipping_artifact"
  | "manager_dropout_artifact"
  | "manager_codec_artifact"
  | "manager_loop_detection"
  | "manager_phoneme_stretch"
  | "manager_syllable_rate"
  | "manager_voice_quality"
  | "manager_timbre_drift"
  | "manager_speech_artifact_anomaly"
  | "manager_speaking_time_percentage"
  | "manager_derived_strict_logic"
  | "manager_derived_aggregate"
  | "manager_sql_float"

const CATEGORY_BY_METRIC_MANAGER: Record<string, string[]> = {
  Deterministic: [
    "manager_api_state",
    "manager_transcript_regex_match",
    "manager_match_expected_sim_wrapper",
    "manager_metadata_field_metric",
    "manager_custom_agent_needs_reprompting",
    "manager_end_reason",
    "manager_agent_needs_reprompting",
    "manager_agent_fails_to_respond",
    "manager_user_frustration",
    "manager_agent_refusal",
    "manager_words_per_message_with_threshold",
    "manager_transcription_error",
    "manager_music_detection",
    "manager_ivr_flow_adherence",
  ],
  Statistical: [
    "manager_phonemes_per_second",
    "manager_syllable_rate",
    "manager_phoneme_stretch",
    "manager_words_per_message",
    "manager_latency",
    "manager_time_to_first_audio",
    "audio_duration_metric_manager",
    "turn_count_metric_manager",
    "interruption_rate_manager",
    "manager_voice_quality",
    "manager_dropout_artifact",
    "manager_codec_artifact",
    "manager_clipping_artifact",
    "manager_loop_detection",
    "manager_speech_artifact_anomaly",
    "manager_abrupt_pitch_changes",
    "manager_pitch_variability",
    "manager_lufs",
    "manager_terminal_contour",
    "manager_non_expressive_pauses",
    "manager_volume_pitch_misalignment",
    "manager_volume",
    "manager_signal_to_noise_ratio",
    "manager_audio_frequency",
    "manager_custom_audio_frequency",
    "manager_vocal_fry",
    "manager_spectrogram_pitch_analysis",
    "manager_speaking_time_percentage",
    "manager_agent_repeats_itself",
  ],
  "ML Model": [
    "manager_audio_sentiment",
    "manager_timbre_drift",
    "manager_sentimentanalysis",
  ],
  "LLM Judge": [
    AGENT_JUDGE_MANAGER,
    AGENT_JUDGE_CATEGORICAL_MANAGER,
    AGENT_JUDGE_NUMERICAL_MANAGER,
    "manager_rubric_decision_tree",
    "manager_default_categorical",
    "manager_audio_llm_numerical",
    "manager_numerical_llm_judge",
    "manager_audio_llm_binary",
    "manager_llm_binary",
    "manager_audio_llm_categorical",
    "manager_composite_evaluation",
  ],
  Trace: [
    "manager_trace_audio_upload_stt_wer",
    "manager_llm_token_usage",
    "manager_trace_stt_wer",
    "manager_stt_ttfb",
    "manager_tool_call_rate",
    "manager_custom_trace",
    "manager_tts_ttfb",
    "manager_llm_ttfb",
    "manager_custom_agent_fails_to_respond",
    "manager_llm_cost_estimate",
  ],
  "Custom SQL": ["manager_sql_float"],
}

const managerToCategoryMap = new Map<string, string>()
for (const [category, managers] of Object.entries(CATEGORY_BY_METRIC_MANAGER)) {
  for (const manager of managers) {
    managerToCategoryMap.set(manager, category)
  }
}

export function getCategoryForManager(metricManager: string): string {
  return managerToCategoryMap.get(metricManager) ?? "Other"
}

// Output Type mapping for each metric type
export const OUTPUT_TYPE_BY_METRIC_TYPE: Record<string, string> = {
  [AGENT_JUDGE_MANAGER]: "string",
  [AGENT_JUDGE_CATEGORICAL_MANAGER]: "string",
  [AGENT_JUDGE_NUMERICAL_MANAGER]: "float",
  manager_llm_binary: "string",
  manager_toolcall: "float",
  manager_default_categorical: "string",
  manager_metadata_field_metric: "float",
  manager_numerical_llm_judge: "float",
  manager_audio_llm_judge: "string",
  manager_audio_llm_binary: "string",
  manager_audio_llm_categorical: "string",
  manager_audio_llm_numerical: "float",
  manager_transcript_regex_match: "string",
  manager_user_frustration: "float",
  manager_agent_refusal: "float",
  manager_api_state: "string",
  pause_analysis_metric: "float",
  manager_volume: "float",
  manager_abrupt_pitch_changes: "float",
  manager_pitch_variability: "float",
  manager_lufs: "float",
  manager_terminal_contour: "float",
  manager_volume_pitch_misalignment: "float",
  manager_non_expressive_pauses: "float",
  manager_vocal_fry: "float",
  manager_spectrogram_pitch_analysis: "float",
  manager_words_per_message_with_threshold: "string",
  manager_composite_evaluation: "float",
  manager_custom_trace: "float",
  manager_agent_repeats_itself: "string",
  manager_custom_audio_frequency: "float",
  manager_custom_end_reason: "string",
  manager_custom_agent_needs_reprompting: "float",
  manager_custom_audio_sentiment: "string",
  manager_custom_agent_fails_to_respond: "float",
  manager_rubric_decision_tree: "float",
  manager_ivr_flow_adherence: "float",
  manager_transcription_error: "float",
  manager_clipping_artifact: "float",
  manager_dropout_artifact: "float",
  manager_codec_artifact: "float",
  manager_loop_detection: "float",
  manager_phoneme_stretch: "float",
  manager_syllable_rate: "float",
  manager_voice_quality: "float",
  manager_timbre_drift: "float",
  manager_speech_artifact_anomaly: "float",
  manager_speaking_time_percentage: "float",
  manager_derived_strict_logic: "string",
  manager_derived_aggregate: "float",
  manager_sql_float: "float",
}
